/**
 * The shared surface stylesheet, as a string.
 *
 * # Why a string and not `ui.css`
 *
 * `q03.css` opens with the opposite argument, and it is right about its own case:
 * a screen stylesheet imported by Vite is folded into the one stylesheet the page
 * already requests, is parsed by the CSS parser instead of by JavaScript, and
 * costs no round trip. Nothing here disagrees with that.
 *
 * What tipped this file the other way is that **an imported `.css` file does not
 * exist during the tests.** Vitest's `css` option defaults to `false`, so a CSS
 * import in a component under test resolves to nothing at all. Every rule below
 * would be invisible to jsdom, and with it the one thing this file is most
 * responsible for: the 48px tap floor. That floor is not a preference — a `−`
 * narrower than 48px between two other controls silently removed a paid modifier
 * from a real bill once already, which is why `test/guest-defects.test.tsx`
 * asserts `getComputedStyle(button).minWidth` rather than grepping a class name.
 * A primitive whose geometry cannot be asserted is a primitive whose geometry
 * will drift, thirteen screens at a time.
 *
 * So: one string, mounted once, in the document where jsdom and the browser both
 * see it. The cost is a few kilobytes of gzipped JavaScript and one `<style>`
 * insertion for the life of the page — not per mount, which is the expensive
 * shape `q03.css` warned about (see `UiStyles`, which uses React 19's `href` +
 * `precedence` de-duplication so that mounting it from every screen still yields
 * exactly one element in `<head>`).
 *
 * # Rules for editing this file
 *
 * * **No hex literal, ever.** Every colour is a `var(--…)`. The tokens come from
 *   `pokpokTheme` (astryx) or, for the handful astryx does not carry, from
 *   `src/theme/surfaceTokens.ts` — which explains each one.
 * * **No size below 16px on body copy, no line-height below 1.6 on it.** Thai
 *   tone marks and descenders clip. De-emphasis is colour and weight; that is
 *   what `.pk-muted`, `.pk-hint` and `font-weight` are for, and the design's
 *   four tiers of small text collapse onto them.
 * * **48px minimum on anything a finger touches**, on both axes. If a drawn row
 *   does not fit a 48px control, the row is redrawn — the control is not shrunk.
 * * **No shadows.** The design is flat: white cards on Sticky Rice, separated by
 *   a warm hairline. `--pokpok-shadow-low` exists and is not for this surface.
 * * **No animation** beyond what `prefers-reduced-motion` in `global.css` would
 *   kill anyway. Cheap phone, direct sunlight, 4G.
 */
export const UI_CSS = `
/* ---------------------------------------------------------------- shell --
 * Every screen is a column: a top bar that does not scroll, a body, and
 * optionally a footer pinned by flex layout. NOT by position:fixed — a fixed bar
 * on a mobile browser fights the collapsing URL bar and the on-screen keyboard,
 * and this surface runs on a guest's own phone where both are unavoidable.
 */
.pk-screen {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  /* The other half of the pinned footer: a flex item defaults to
     min-height:auto, which means "never smaller than my content" — so this grew
     to the height of the menu and the scroll region inside it had nothing to
     scroll within. See .pk-body--scroll below. */
  min-height: 0;
  background: var(--color-background-body);
}

.pk-topbar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: var(--pokpok-space-2);
  min-height: 56px;
  padding: 0 var(--pokpok-gutter);
}
.pk-topbar-leading {
  display: flex;
  align-items: center;
  min-width: 0;
}
.pk-topbar-trailing {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: var(--pokpok-space-2);
}

/* The back affordance. It was a bare glyph run with no padding in the design —
   about 20px of hit area — which is a control a wet thumb misses. */
.pk-back {
  display: inline-flex;
  align-items: center;
  gap: var(--pokpok-space-1);
  min-height: 48px;
  min-width: 48px;
  padding-right: var(--pokpok-space-2);
  color: var(--color-text-secondary);
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
}
.pk-back-glyph {
  font-size: 1.25em;
  line-height: 1;
}

/* The venue name occupies the slot the back button would have, on the one
   screen (the menu) that has nowhere to go back to. */
.pk-venue {
  color: var(--color-text-secondary);
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* The venue's own logo, when branding carries one. Capped to the top bar's
   height and its longest reasonable width; object-fit keeps the mark's ratio so
   a tall or wide logo neither stretches nor overflows the bar. */
.pk-venue-logo {
  display: block;
  height: 28px;
  width: auto;
  max-width: 180px;
  object-fit: contain;
}

.pk-body {
  padding: var(--pokpok-space-2) var(--pokpok-gutter) var(--pokpok-space-6);
}
/* Own scroll region — only for a screen with a pinned footer.

   min-height:0 here is necessary and was not sufficient: a flex item's default
   min-height:auto means it refuses to shrink below its content, and that applies
   to .pk-screen above it too. Without the matching rule there, the screen grew
   to the height of the menu, the "scroll region" was never smaller than what it
   contained, nothing scrolled inside it, and the bar the handoff README calls
   pinned sat at the end of the *document* — visible only after scrolling past
   every dish. Three screens shipped that way: Q3, Q4 and Q5. Fixed 2026-08-14;
   see #root in global.css for the other half, which bounds the column at all. */
.pk-body--scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
/* Grows to fill, scrolls with the document. The default for footerless screens:
   the browser's URL bar collapses on a document scroll and does not on an inner
   one, which is real vertical space on a small phone. */
.pk-body--grow {
  flex: 1 1 auto;
}
/* A short, centred message: the success screen, and the two terminal notices. */
.pk-body--center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: var(--pokpok-space-4);
  padding-left: var(--pokpok-space-6);
  padding-right: var(--pokpok-space-6);
}

.pk-footer {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--pokpok-space-2);
  padding: var(--pokpok-space-4) var(--pokpok-gutter);
  background: var(--color-background-body);
}
/* A rule above the footer where content scrolls under it, and none where the
   content is centred or short — the design draws both and means both. */
.pk-footer--bordered {
  border-top: 1px solid var(--pokpok-color-border-warm);
}
.pk-footer--plain {
  padding-top: 0;
}

/* Sticky search + chips on the menu. Opaque backfill, or list rows show through
   as they pass under it.

   top is -space-2, not 0, and that is not a nudge: the scroll container
   (.pk-body) pads its top by space-2, and a sticky sticks to that container's
   content edge — so top:0 pins the bar space-2 BELOW the scrollport and leaves a
   transparent strip of the container's own top padding above it, which the list
   rows scroll visibly through as you slide down (owner, 2026-08-26). Pinning at
   -space-2 lifts the bar flush to the scrollport top; the padding-top below pays
   the same space back inside the opaque backfill, where a row can never show. It
   is the vertical twin of the margin-inline/padding-inline trick .q3-sticky uses
   to close the same gap on the left and right edges. */
.pk-sticky {
  position: sticky;
  top: calc(-1 * var(--pokpok-space-2));
  z-index: 5;
  background: var(--color-background-body);
  padding-top: var(--pokpok-space-2);
}

/* ----------------------------------------------------------------- type --
 * Two sizes came from the theme (16px body, 27.648px heading-1); this surface
 * adds exactly one step between them. See --pokpok-text-title-size in
 * src/theme/surfaceTokens.ts for why there is one and not four.
 */
.pk-h1 {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: var(--text-heading-1-size);
  line-height: var(--text-heading-1-leading);
  margin: 0;
}
.pk-title {
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: var(--pokpok-text-title-size);
  line-height: var(--pokpok-text-title-leading);
  margin: 0;
}
.pk-strong {
  font-weight: 600;
}
.pk-muted {
  color: var(--color-text-secondary);
}
.pk-hint {
  color: var(--color-text-disabled);
}

/* Layout helpers, so thirteen screens do not each write the same four lines. */
.pk-stack {
  display: flex;
  flex-direction: column;
  gap: var(--pokpok-space-2);
}
.pk-stack--wide {
  gap: var(--pokpok-space-4);
}
.pk-split {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--pokpok-space-4);
}
.pk-divider {
  height: 1px;
  border: 0;
  margin: 0;
  background: var(--pokpok-color-border-warm);
}
/* A row on the page ground, separated by a rule rather than boxed in a card:
   the status list and the receipt list are both drawn this way. */
.pk-ruled {
  border-top: 1px solid var(--pokpok-color-border-warm);
  padding: var(--pokpok-space-2) 0;
}

/* --------------------------------------------------------------- money --
 * The only sanctioned path to Pandan on this surface, and the reason no screen
 * needs to know what colour verified money is.
 */
.pk-money {
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  font-weight: 700;
  white-space: nowrap;
}
/* Neutral and pending are the same ink, deliberately. "Pending" is an ordinary
   amount that nobody has verified yet — a menu price, a cart estimate, a bill in
   flight — and painting it amber would tell a guest something is wrong with a
   number that is simply a number. The amber belongs on the *word* beside it
   (StatusPill tone="warning"), where it says what is unresolved. */
.pk-money--neutral,
.pk-money--pending {
  color: var(--color-text-primary);
}
.pk-money--verified {
  color: var(--pokpok-color-money-verified);
}
.pk-money--refunded {
  color: var(--color-text-secondary);
  text-decoration: line-through;
}

/* -------------------------------------------------------------- buttons --
 * Four weights, because the design uses four and each one is doing a job:
 * solid red (the one forward action), ink outline (the alternative), warm
 * hairline (a way out that is not an action), and text (an aside). Destructive
 * is a fifth and is never a fill.
 *
 * Class selectors throughout, so these beat global.css's bare "button" rules
 * regardless of which stylesheet the bundler emits first.
 */
.pk-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--pokpok-space-2);
  min-height: 48px;
  min-width: 48px;
  padding: var(--pokpok-space-2) var(--pokpok-space-4);
  border: var(--pokpok-border-width-control) solid transparent;
  border-radius: var(--radius-container);
  background: none;
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: var(--text-body-size);
  font-weight: 600;
  line-height: 1.6;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
}
.pk-btn--block {
  width: 100%;
}

.pk-btn--primary {
  min-height: 56px;
  border-color: transparent;
  background: var(--color-accent);
  color: var(--color-on-accent);
  font-weight: 700;
}
/* A desaturated fill, not an opacity: the label has to stay legible, because it
   still says what the button will do once the guest answers the question above
   it. Pair it with "disabledReason" on "Button" — a control that refuses a tap
   and does not say why is the defect this state is most likely to ship with. */
.pk-btn--primary:disabled,
.pk-btn--primary[aria-disabled='true'] {
  background: var(--pokpok-color-accent-disabled);
  color: var(--color-on-accent);
  cursor: default;
}

.pk-btn--secondary {
  border-color: var(--color-text-primary);
  color: var(--color-text-primary);
}
.pk-btn--tertiary {
  border-color: var(--pokpok-color-border-warm);
  color: var(--color-text-secondary);
}
.pk-btn--text {
  min-height: 48px;
  border-color: transparent;
  color: var(--color-text-secondary);
  text-decoration: underline;
  text-underline-offset: 3px;
}
/* Outline only, in Dried Chili, and the caller is required to pass a word. */
.pk-btn--destructive {
  border-color: var(--color-error);
  color: var(--color-error);
  background: transparent;
}
.pk-btn:disabled:not(.pk-btn--primary),
.pk-btn[aria-disabled='true']:not(.pk-btn--primary) {
  color: var(--color-text-disabled);
  border-color: var(--pokpok-color-border-warm);
  cursor: default;
}

/* ----------------------------------------------------------------- card --
 * Flat. White on Sticky Rice, one warm hairline, no shadow anywhere.
 */
.pk-card {
  border: 1px solid var(--pokpok-color-border-warm);
  border-radius: var(--radius-container);
  background: var(--color-background-card);
  padding: var(--pokpok-space-4);
}
.pk-card--tight {
  padding: var(--pokpok-space-2) var(--pokpok-space-4);
}
.pk-card--flush {
  padding: 0;
}
/* The one card allowed to be green, and only for server-verified money. */
.pk-card--verified {
  background: var(--pokpok-color-money-verified-surface);
  border-color: var(--pokpok-color-money-verified-border);
}
/* The "you will pay at the counter" notice. Accent-muted, and a warm dark red
   ink that is not the error colour, because nothing has gone wrong. */
.pk-card--accent {
  background: var(--color-accent-muted);
  border-color: var(--pokpok-color-accent-muted-border);
  color: var(--pokpok-color-accent-muted-ink);
}
.pk-card--warning {
  background: var(--color-warning-muted);
  border-color: var(--color-warning);
  color: var(--color-text-primary);
}

.pk-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--pokpok-space-2);
  padding-bottom: var(--pokpok-space-2);
  border-bottom: 1px solid var(--pokpok-color-border-warm);
}
.pk-section-title {
  font-size: var(--text-body-size);
  font-weight: 700;
  margin: 0;
}

/* ----------------------------------------------------------------- pill --
 * A label, never a control. No hover, no tap target, no onClick.
 */
.pk-pill {
  display: inline-flex;
  align-items: center;
  gap: var(--pokpok-space-1);
  align-self: flex-start;
  padding: 0 var(--pokpok-space-2);
  border-radius: 9999px;
  font-weight: 600;
  white-space: nowrap;
}
.pk-pill--neutral {
  background: var(--color-neutral);
  color: var(--color-text-primary);
}
.pk-pill--warning {
  background: var(--color-warning-muted);
  color: var(--color-warning);
}
.pk-pill--info {
  background: var(--color-success-muted);
  color: var(--color-success);
}
.pk-pill--verified {
  background: var(--pokpok-color-money-verified-surface);
  color: var(--pokpok-color-money-verified);
}

/* ----------------------------------------------------------------- chip --
 * Horizontally scrollable, always exactly one selected, never zero.
 * Selection is a red outline and a tinted fill; it is not the screen's one solid
 * red action and must not look like one.
 */
.pk-chiprow {
  display: flex;
  gap: var(--pokpok-space-2);
  padding: var(--pokpok-space-2) 0;
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}
.pk-chiprow::-webkit-scrollbar {
  display: none;
}
.pk-chip {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  min-width: 48px;
  padding: 0 var(--pokpok-space-4);
  border: var(--pokpok-border-width-control) solid var(--pokpok-color-border-warm);
  border-radius: 9999px;
  background: var(--color-background-card);
  color: var(--color-text-primary);
  font-size: var(--text-body-size);
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}
.pk-chip[aria-pressed='true'] {
  border-color: var(--color-accent);
  background: var(--color-accent-muted);
  color: var(--color-text-accent);
}

/* ------------------------------------------------------------- quantity --
 * 48x48 on both axes, and the row is drawn around that rather than the other
 * way round. The design drew 28x28; a horizontal slip between - and + is a
 * wrong quantity on a real bill, and it has happened.
 */
.pk-qty {
  display: inline-flex;
  align-items: center;
  gap: var(--pokpok-space-2);
}
.pk-qty-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  min-width: 48px;
  min-height: 48px;
  padding: 0;
  border: 1px solid var(--pokpok-color-border-warm);
  border-radius: var(--pokpok-radius-2);
  background: var(--color-background-muted);
  color: var(--color-text-primary);
  font-size: var(--text-body-size);
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
}
.pk-qty-btn:disabled {
  color: var(--color-text-disabled);
  cursor: default;
}
.pk-qty-value {
  min-width: 40px;
  text-align: center;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
}

/* ----------------------------------------------------------- option row --
 * A real <input> inside a <label>, not a button with aria-checked: the native
 * control brings keyboard behaviour, grouping and announcement for free, and the
 * whole 48px label is the tap target.
 */
.pk-option {
  display: flex;
  align-items: center;
  gap: var(--pokpok-space-4);
  min-height: 48px;
  padding: var(--pokpok-space-1) 0;
  cursor: pointer;
}
.pk-option[data-disabled='true'] {
  color: var(--color-text-disabled);
  cursor: default;
}
.pk-option-mark {
  flex: 0 0 auto;
  -webkit-appearance: none;
  appearance: none;
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  margin: 0;
  border: 2px solid var(--color-border-emphasized);
  background: var(--color-background-card);
  cursor: inherit;
}
.pk-option-mark[type='radio'] {
  border-radius: 9999px;
}
.pk-option-mark[type='checkbox'] {
  border-radius: 6px;
}
.pk-option-mark[type='radio']:checked {
  border-color: var(--color-accent);
}
.pk-option-mark[type='radio']:checked::after {
  content: '';
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  background: var(--color-accent);
}
/* Ink, not red: a chosen extra must not compete with the screen's one red
   action, and a checkbox is not a forward step. */
.pk-option-mark[type='checkbox']:checked {
  border-color: var(--color-text-primary);
  background: var(--color-text-primary);
}
.pk-option-mark[type='checkbox']:checked::after {
  content: '';
  width: 5px;
  height: 10px;
  margin-top: -2px;
  border: solid var(--color-on-dark);
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.pk-option-mark[aria-disabled='true'] {
  border-color: var(--color-text-disabled);
  background: var(--color-background-muted);
}
.pk-option-label {
  flex: 1 1 auto;
  min-width: 0;
}
.pk-option-note {
  flex: 0 0 auto;
  color: var(--color-text-secondary);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  white-space: nowrap;
}
.pk-option[data-disabled='true'] .pk-option-note {
  color: var(--color-text-disabled);
}

/* --------------------------------------------------------------- fields --
 * 16px is a floor here for a second reason: iOS zooms the whole page when a
 * focused input is smaller, and a zoomed page on a menu is a guest lost.
 */
.pk-input,
.pk-textarea {
  width: 100%;
  min-height: 48px;
  border: 1px solid var(--pokpok-color-border-warm);
  border-radius: var(--radius-container);
  background: var(--color-background-card);
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: var(--text-body-size);
  line-height: 1.6;
}
.pk-input {
  padding: 0 var(--pokpok-space-4);
}
.pk-textarea {
  min-height: 96px;
  padding: var(--pokpok-space-2) var(--pokpok-space-4);
  resize: vertical;
}

/* ---------------------------------------------------------------- photo --
 * The stripe is the background BEHIND a real image, never a box on its own. An
 * item with no photo renders no element at all — the no-photo layout is the
 * layout on this product, and a guard test enforces it.
 */
.pk-photo {
  display: block;
  background: var(--pokpok-photo-placeholder);
  object-fit: cover;
}
.pk-photo--thumb {
  flex: 0 0 auto;
  width: 56px;
  height: 56px;
  border-radius: var(--pokpok-radius-2);
}
/* Square, because the photograph is (owner directive, 2026-08-14).
 *
 * This was a 180px band, which is what the design package draws. Against real
 * menu photography it was the wrong box: every uploaded image is square — the
 * upload path produces one and the merchants' own phones shoot one — so a
 * 180px-tall strip with object-fit:cover threw away the top and bottom thirds of
 * the dish and kept a horizontal slice of the plate. On a fried egg that is a
 * band of white.
 *
 * aspect-ratio rather than a second fixed height, so the box follows the image
 * instead of the image being cut to fit the box.
 *
 * max-width because this surface has no column cap of its own: on a desktop
 * window a full-width square hero is 900px of egg. A phone is unaffected — 390px
 * wide is below the cap — and the guest surface is a phone. Centred so the
 * desktop case does not look broken. */
.pk-photo--hero {
  width: 100%;
  max-width: 420px;
  aspect-ratio: 1;
  height: auto;
  margin-inline: auto;
  border-radius: var(--radius-container);
}

/* ------------------------------------------------------------- skeleton --
 * The same grey and the same shape as the pre-bundle skeleton in index.html. A
 * second loading look on the same page reads as two different pages loading.
 * Static, never shimmering: an animation costs frames on the phone least able to
 * spare them and buys nothing.
 */
.pk-skeleton {
  height: 16px;
  border-radius: var(--pokpok-radius-2);
  background: var(--color-skeleton);
}
.pk-skeleton-row {
  display: flex;
  align-items: center;
  gap: var(--pokpok-space-4);
  padding: var(--pokpok-space-4) 0;
  border-bottom: 1px solid var(--pokpok-color-border-warm);
}

/* ---------------------------------------------------------------- sheet --
 * Sticky Rice, not white: the sheet is the same ground raised, not a card.
 */
.pk-scrim {
  position: fixed;
  inset: 0;
  z-index: 30;
  display: flex;
  align-items: flex-end;
  background: var(--color-overlay);
}
.pk-sheet {
  width: 100%;
  max-height: 85dvh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--pokpok-space-4);
  padding: var(--pokpok-space-6) var(--pokpok-gutter);
  padding-bottom: calc(var(--pokpok-space-6) + env(safe-area-inset-bottom));
  border-radius: var(--radius-container) var(--radius-container) 0 0;
  background: var(--color-background-body);
}

/* ------------------------------------------------------ terminal notice --
 * One ring, one headline, one body, then the way out. The success screen's
 * shape, reused for invalid-QR and venue-closed.
 */
.pk-terminal {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: var(--pokpok-space-4);
}
.pk-terminal-ring {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 9999px;
  border: 2px solid currentColor;
  font-size: var(--pokpok-text-title-size);
  line-height: 1;
}
/* Butterfly Pea. The done colour that is not money — the send-confirmed tick. */
.pk-terminal-ring--info {
  color: var(--color-success);
}
.pk-terminal-ring--warning {
  color: var(--color-warning);
}
.pk-terminal-ring--error {
  color: var(--color-error);
}

/* ----------------------------------------------------------------- rail --
 * Kitchen progress. NOT drawn anywhere in the design package — extrapolated from
 * its vocabulary: page-ground rows, warm hairline, weight and colour carrying
 * state rather than size. It is never green: "ready to serve" is a prep state,
 * not verified money, and moneyTone.ts says so in as many words.
 */
.pk-rail {
  list-style: none;
  margin: 0;
  padding: 0;
}
.pk-rail-step {
  display: flex;
  align-items: center;
  gap: var(--pokpok-space-4);
  padding: var(--pokpok-space-2) 0;
  border-top: 1px solid var(--pokpok-color-border-warm);
  color: var(--color-text-secondary);
}
.pk-rail-step:first-child {
  border-top: none;
}
.pk-rail-step[data-state='done'] {
  color: var(--color-text-primary);
}
.pk-rail-step[data-state='current'] {
  color: var(--color-text-primary);
  font-weight: 700;
}
.pk-rail-mark {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  border: 2px solid var(--color-border-emphasized);
}
.pk-rail-step[data-state='done'] .pk-rail-mark {
  border-color: var(--color-success);
  background: var(--color-success);
}
.pk-rail-step[data-state='current'] .pk-rail-mark {
  border-color: var(--color-success);
}
`;
